import { Loader2 } from "lucide-react";
import type { JSX, KeyboardEvent } from "react";

import { cn } from "../../lib/cn.js";
import { Checkbox } from "../ui/checkbox.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table.js";

import { getDataTableColumnClassName, getDataTableColumnStyle } from "./DataTable.helpers.js";
import type { DataTableProps } from "./DataTable.types.js";
import { EmptyState } from "./EmptyState.js";
import { LoadingState } from "./LoadingState.js";

export type { DataTableColumn, DataTableProps } from "./DataTable.types.js";

export function DataTable<T>({
  columns,
  rows,
  loading,
  refetching,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  rowKey,
  className,
  rowClassName,
  onRowClick,
  getRowHref,
  selectable,
  selectedKeys,
  onSelectionChange,
  isRowSelectable,
}: DataTableProps<T>): JSX.Element {
  if (loading) return <LoadingState variant="skeleton" rows={4} />;
  if (!rows || rows.length === 0)
    return <EmptyState title={emptyTitle} description={emptyDescription} />;

  const selected = selectedKeys ?? new Set<string>();
  const selectableRows = rows.filter((r) => isRowSelectable?.(r) ?? true);
  const selectedOnPage = selectableRows.filter((r) => selected.has(rowKey(r))).length;
  const allOnPageSelected =
    selectableRows.length > 0 && selectedOnPage === selectableRows.length;
  // Indeterminate whenever the page is partly selected, so the header conveys
  // "some" rather than lying with an unchecked box.
  const headerChecked: boolean | "indeterminate" = allOnPageSelected
    ? true
    : selectedOnPage > 0
      ? "indeterminate"
      : false;

  function toggleAllOnPage(): void {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (allOnPageSelected) {
      for (const r of selectableRows) next.delete(rowKey(r));
    } else {
      for (const r of selectableRows) next.add(rowKey(r));
    }
    onSelectionChange(next);
  }

  function toggleRow(key: string): void {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  }

  function activateRow(row: T, href: string | undefined): void {
    if (onRowClick) {
      onRowClick(row);
      return;
    }

    if (href) window.location.assign(href);
  }

  function handleRowKeyDown(
    event: KeyboardEvent<HTMLTableRowElement>,
    row: T,
    href: string | undefined,
  ): void {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    activateRow(row, href);
  }

  return (
    <div className="relative">
      <div
        className={cn(
          "overflow-x-auto rounded-lg border border-border/80 bg-background shadow-xs",
          refetching && "pointer-events-none opacity-60 transition-opacity",
          className,
        )}
        aria-busy={refetching || undefined}
      >
        <Table className="min-w-[720px]">
        <TableHeader className="bg-muted/60">
          <TableRow>
            {selectable ? (
              <TableHead className="h-11 w-10 border-b border-border/80 px-4">
                <Checkbox
                  checked={headerChecked}
                  onCheckedChange={toggleAllOnPage}
                  aria-label={allOnPageSelected ? "Clear selection" : "Select all on this page"}
                />
              </TableHead>
            ) : null}
            {columns.map((c) => (
              <TableHead
                key={c.key}
                className={cn(
                  "h-11 border-b border-border/80 px-4 text-xs font-medium text-muted-foreground",
                  getDataTableColumnClassName(c, "header"),
                )}
                style={getDataTableColumnStyle(c)}
              >
                {c.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const href = getRowHref?.(row);
            const interactive = Boolean(onRowClick || href);
            const key = rowKey(row);
            const rowSelectable = selectable && (isRowSelectable?.(row) ?? true);
            const isSelected = selected.has(key);

            return (
              <TableRow
                key={key}
                data-state={isSelected ? "selected" : undefined}
                className={cn(
                  "border-border/70 bg-background transition-colors hover:bg-muted/50",
                  isSelected && "bg-primary/5 hover:bg-primary/10",
                  interactive &&
                    "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                  rowClassName?.(row),
                )}
                onClick={interactive ? () => activateRow(row, href) : undefined}
                onKeyDown={interactive ? (event) => handleRowKeyDown(event, row, href) : undefined}
                role={href ? "link" : onRowClick ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
              >
                {selectable ? (
                  // stopPropagation so ticking a box never triggers the row's
                  // click-through navigation.
                  <TableCell
                    className="w-10 px-4 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={!rowSelectable}
                      onCheckedChange={() => toggleRow(key)}
                      aria-label={isSelected ? "Deselect row" : "Select row"}
                    />
                  </TableCell>
                ) : null}
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={cn(
                      "px-4 py-3 text-sm text-foreground",
                      getDataTableColumnClassName(c, "cell"),
                    )}
                    style={getDataTableColumnStyle(c)}
                  >
                    {c.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
      {refetching ? (
        <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-background/95 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm ring-1 ring-border/70">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </span>
        </div>
      ) : null}
    </div>
  );
}
